import React, { useState, useEffect } from 'react';
import { auth } from '../config/firebase';
import { signInAnonymously } from 'firebase/auth';
import firebaseService from '../services/firebaseService';
import useTemporaryMessage from '../hooks/useTemporaryMessage';

const LandingPage = ({ onGameStart }) => {
    const [playerName, setPlayerName] = useState(() => {
        return localStorage.getItem('guesswhat_username') || '';
    });
    const [roomId, setRoomId] = useState('');
    const [error, showError] = useTemporaryMessage(3000);
    const [shareUrl, setShareUrl] = useState('');

    useEffect(() => {
        // Sign in anonymously when component mounts
        signInAnonymously(auth).catch((error) => {
            console.error('Error signing in anonymously:', error);
            let errorMessage = 'Failed to initialize';

            switch (error.code) {
                case 'auth/admin-restricted-operation':
                    errorMessage = 'Anonymous sign-in is not enabled. Please contact the administrator.';
                    break;
                case 'auth/operation-not-allowed':
                    errorMessage = 'Anonymous sign-in is not enabled. Please contact the administrator.';
                    break;
                default:
                    errorMessage = error.message;
            }

            showError(errorMessage);
        });
    }, []);

    const handleCreateGame = async (e) => {
        e.preventDefault();
        const trimmedName = playerName.trim();
        if (!trimmedName) {
            showError('Please enter a player name');
            return;
        }

        localStorage.setItem('guesswhat_username', trimmedName);

        try {
            // Ensure user is authenticated
            if (!auth.currentUser) {
                await signInAnonymously(auth);
            }

            // Create game through API
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/games`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    playerName: trimmedName,
                    playerId: auth.currentUser.uid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to create game');
            }

            const { roomId } = await response.json();

            // Join the game with Firebase
            const player = {
                id: auth.currentUser.uid,
                name: trimmedName
            };

            await firebaseService.joinGame(roomId, player);

            // Subscribe to game updates
            firebaseService.subscribeToGame(roomId, (game) => {
                if (game) {
                    const url = `${window.location.origin}/room/${roomId}`;
                    setShareUrl(url);
                    onGameStart(game);
                } else {
                    showError('Game was removed');
                }
            });
        } catch (error) {
            console.error('Error creating game:', error);
            showError('Failed to create game: ' + error.message);
        }
    };

    const handleJoinGame = async (e) => {
        e.preventDefault();
        const trimmedName = playerName.trim();
        const trimmedRoomId = roomId.trim().toUpperCase();

        if (!trimmedName || !trimmedRoomId) {
            showError('Please enter both player name and room code');
            return;
        }

        if (trimmedRoomId.length !== 6) {
            showError('Room code must be 6 characters long');
            return;
        }

        localStorage.setItem('guesswhat_username', trimmedName);

        try {
            const player = {
                id: auth.currentUser.uid,
                name: trimmedName
            };

            await firebaseService.joinGame(trimmedRoomId, player);

            // Subscribe to game updates
            firebaseService.subscribeToGame(trimmedRoomId, (game) => {
                if (game) {
                    onGameStart(game);
                } else {
                    showError('Game not found or was removed');
                }
            });
        } catch (error) {
            console.error('Error joining game:', error);
            showError('Failed to join game: ' + error.message);
        }
    };

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            showError('Room URL copied to clipboard!');
        }).catch(() => {
            showError('Failed to copy URL');
        });
    };

    return (
        <>
            <div className="landing-content">
                <div className="landing-header">
                    <h1 className="game-title">
                        Guess What?!
                    </h1>
                    <p className="game-subtitle">AI images, wild guesses, and endless fun! Compete with friends to crack the prompt!</p>
                </div>

                {error && (
                    <div className="error-message">{error}</div>
                )}

                {shareUrl && (
                    <div className="share-url-container">
                        <input
                            type="text"
                            value={shareUrl}
                            readOnly
                            className="share-url-input"
                            aria-label="Game URL"
                        />
                        <button
                            onClick={handleCopyUrl}
                            className="secondary-button"
                            aria-label="Copy game URL"
                        >
                            <span className="button-icon">📋</span>
                            Copy URL
                        </button>
                    </div>
                )}

                <div className="form-container">
                    <div className="input-group">
                        <input
                            type="text"
                            id="playerName"
                            value={playerName}
                            onChange={(e) => setPlayerName(e.target.value)}
                            placeholder="Your name"
                            maxLength={20}
                        />
                    </div>

                    <div className="game-options">
                        <div className="start-game">
                            <form onSubmit={handleCreateGame}>
                                <button
                                    type="submit"
                                    className="primary-button"
                                    disabled={!playerName.trim()}
                                >
                                    Start New Game
                                </button>
                            </form>
                        </div>
                        <div className="vertical-divider-container">
                            <div className="vertical-divider-line"></div>
                            <div className="vertical-divider-text">or</div>
                            <div className="vertical-divider-line"></div>
                        </div>
                        <form onSubmit={handleJoinGame} className="input-group room-input-group">
                            <input
                                type="text"
                                id="roomId"
                                value={roomId}
                                onChange={(e) => setRoomId(e.target.value.toUpperCase())}
                                placeholder="Room code"
                                maxLength={6}
                                className="room-input"
                            />
                            <button
                                type="submit"
                                className="primary-button"
                                disabled={!playerName.trim() || !roomId.trim()}
                            >
                                Join Game
                            </button>
                        </form>
                    </div>
                </div>
            </div>
            <div className="attribution">
                Built with <a href="https://openai.com" target="_blank" rel="noopener noreferrer">OpenAI</a> & <a href="https://cursor.sh" target="_blank" rel="noopener noreferrer">Cursor</a> by <a href="https://lauren.wang" target="_blank" rel="noopener noreferrer">lauren.wang</a>
            </div>
        </>
    );
};

export default LandingPage; 